// The closing robot act on every public content page: one big line, a CTA,
// and the androids performing with a set piece that fits the page. Rendered
// once by App (after the routed page) from the per-route table below.
// Marked data-robot-stage="outro" so form pages that end in one still keep
// their form-assistant android (Experience3D ignores outros for that).
import { useEffect, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ActorSlot, Label, PropSlot, easeOut, primaryBtn, type CastMember } from "./kit";

interface OutroAct {
  label: string;
  title: string;
  body: string;
  cta: { label: string; to: string };
  cast: CastMember[];
  prop: string;
}

const ACTS: Array<[string, OutroAct]> = [
  ["/about", {
    label: "Our promise",
    title: "Outcomes over optics. Built here, for here.",
    body: "We measure success by impact on revenue, cost, risk and capability, and we run our own products before we recommend them.",
    cta: { label: "Book a consultation", to: "/consultation-intro" },
    cast: [{ who: "male", pose: "akimbo", face: 0.25 }, { who: "female", pose: "akimbo", face: -0.25 }],
    prop: "globe",
  }],
  ["/news", {
    label: "Stay close",
    title: "The week in African AI, explained.",
    body: "Join practitioners across Ghana, Nigeria and Rwanda who read us every week.",
    cta: { label: "Join the AI Hub", to: "/hub-registration" },
    cast: [{ who: "female", pose: "hold", face: 0.3 }],
    prop: "orbit",
  }],
  ["/news/:slug", {
    label: "Keep reading",
    title: "More from the newsroom.",
    body: "Essays, field notes and the weekly AI newsletter from the HIBANE team.",
    cta: { label: "All posts", to: "/news" },
    cast: [{ who: "female", pose: "hold", face: 0.3 }],
    prop: "globe",
  }],
  ["/contact", {
    label: "Prefer to talk?",
    title: "Call us. A person answers.",
    body: "24hrs support on +233 596 992 641, WhatsApp on +233 544 049 500, or info@hibane.ai.",
    cta: { label: "Call 24hrs support", to: "tel:+233596992641" },
    cast: [{ who: "male", pose: "phone", face: 0.3 }, { who: "female", pose: "wave", face: -0.3 }],
    prop: "signal",
  }],
  ["/media", {
    label: "One studio",
    title: "From script to screen.",
    body: "Pre-production, shoot, edit and secure delivery under one roof in East Legon.",
    cta: { label: "Start booking", to: "#booking" },
    cast: [{ who: "male", pose: "console", face: 0.3 }, { who: "female", pose: "present", face: -0.3 }],
    prop: "carousel",
  }],
  ["/media/portfolio", {
    label: "Your turn",
    title: "Have a story to tell?",
    body: "Bring the brief. We'll bring the crew, the studio and the delivery.",
    cta: { label: "Book a project", to: "/media#booking" },
    cast: [{ who: "male", pose: "present", face: 0.3 }],
    prop: "carousel",
  }],
  ["/hub-registration", {
    label: "Inside the Hub",
    title: "Monthly sessions. Real deployments. No slides.",
    body: "Practitioners sharing what works, and what doesn't, in African AI projects.",
    cta: { label: "Join now", to: "#registration-form" },
    cast: [{ who: "male", pose: "cross", face: 0.25 }, { who: "female", pose: "akimbo", face: -0.25 }],
    prop: "network",
  }],
  ["/trainwithhibane", {
    label: "How we teach",
    title: "Taught by practitioners.",
    body: "Every module comes from a system we built and run, so your team learns what works on Monday.",
    cta: { label: "Register now", to: "#registration-form" },
    cast: [{ who: "female", pose: "welcome", face: 0.2 }],
    prop: "bars",
  }],
  ["/curriculum", {
    label: "Not sure where to start?",
    title: "We'll map the right programme for your team.",
    body: "A short call is enough to match goals, level and budget to a programme.",
    cta: { label: "Book a call", to: "/consultation-intro" },
    cast: [{ who: "male", pose: "think", face: 0.3 }],
    prop: "monoliths",
  }],
  ["/suite", {
    label: "One login",
    title: "Every desk, one source of truth.",
    body: "HR Desk and HIBANE Books share data, so payroll lands in your books automatically.",
    cta: { label: "Start 14-day free trial", to: "/signup?product=bundle" },
    cast: [{ who: "male", pose: "present", face: 0.25 }, { who: "female", pose: "akimbo", face: -0.25 }],
    prop: "carousel",
  }],
  ["/financedesk", {
    label: "Close faster",
    title: "Close the month in days, not weeks.",
    body: "Invoices, bills and multi-currency reports in one place, priced in USD, charged locally.",
    cta: { label: "Start 14-day free trial", to: "/signup?product=finance" },
    cast: [{ who: "male", pose: "present", face: 0.3 }],
    prop: "bars",
  }],
  ["/hrdesk", {
    label: "People first",
    title: "People ops your team will actually use.",
    body: "Records, leave, payroll and performance with a self-service portal for every employee.",
    cta: { label: "Start 14-day free trial", to: "/signup?product=hr" },
    cast: [{ who: "female", pose: "welcome", face: 0.2 }],
    prop: "network",
  }],
  ["/voiceagent", {
    label: "Always on",
    title: "Never miss a call again.",
    body: "Your AI receptionist answers in your brand voice, books appointments and qualifies leads, day and night.",
    cta: { label: "Request free demo", to: "#lead-form" },
    cast: [{ who: "female", pose: "phone", face: 0.3 }],
    prop: "voices",
  }],
];

const actFor = (pathname: string) => ACTS.find(([pattern]) => matchPath({ path: pattern, end: true }, pathname))?.[1];

export default function CineOutro() {
  const { pathname } = useLocation();
  const act = actFor(pathname);
  // wait for the lazily loaded page so the outro never flashes above it
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    const id = window.setTimeout(() => setReady(true), 500);
    return () => clearTimeout(id);
  }, [pathname]);
  if (!act || !ready) return null;

  const pair = act.cast.length > 1;
  const isLink = !/^(#|tel:|mailto:|https?:)/.test(act.cta.to);
  const cta = (
    <>
      {act.cta.label} <ArrowRight className="h-4 w-4" />
    </>
  );

  return (
    <section data-robot-stage="outro" className="relative min-h-[90svh] overflow-hidden border-t border-cx-fg/5 bg-cx-bg2 text-cx-fg">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_45%_55%_at_70%_60%,rgba(74,138,201,0.16),transparent_70%)]" />
      <div className="relative mx-auto grid min-h-[90svh] max-w-7xl items-center gap-10 px-6 py-20 md:grid-cols-12">
        <div className="md:col-span-5">
          <Label>{act.label}</Label>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9, ease: easeOut }}
            className="mt-5 font-cine text-[clamp(2.2rem,4.6vw,4.4rem)] font-semibold leading-[0.98] tracking-[-0.045em] text-balance"
          >
            {act.title}
          </motion.h2>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-cx-fg/65">{act.body}</p>
          <div className="mt-9">
            {isLink ? (
              <Link to={act.cta.to} className={primaryBtn}>{cta}</Link>
            ) : (
              <a href={act.cta.to} className={primaryBtn}>{cta}</a>
            )}
          </div>
        </div>
        <div className="relative h-[52vh] md:col-span-6 md:col-start-7 md:h-[78vh]">
          <PropSlot name={act.prop} className="left-[6%] right-[6%] top-[4%] h-[56%]" />
          {pair ? (
            <>
              <ActorSlot {...act.cast[0]} className="bottom-0 left-[20%] h-[86%] w-[26%]" />
              <ActorSlot {...act.cast[1]} className="bottom-0 left-[54%] h-[82%] w-[26%]" />
            </>
          ) : (
            <ActorSlot {...act.cast[0]} className="bottom-0 left-[34%] h-[88%] w-[32%]" />
          )}
        </div>
      </div>
    </section>
  );
}
